import { SendOutlined, ThunderboltOutlined } from "@ant-design/icons"
import {
  Button,
  Drawer,
  Input,
  Progress,
  Select,
  Slider,
  Tooltip,
  Typography
} from "antd"
import React, { useRef, useState } from "react"

import { IS_PROD } from "../constants/dev"
import { request } from "../services/api.ts"

const { TextArea } = Input
const { Text } = Typography

export function CurlIndicator() {
  if (IS_PROD) return null
  return <Indicator />
}

type HttpMethod = "get" | "post" | "put" | "patch" | "delete"

type ParsedCurl = {
  url: string
  method: HttpMethod
  body?: string
}

const Indicator = () => {
  const [open, setOpen] = useState(false)
  const [curl, setCurl] = useState("")
  const [bodyTemplate, setBodyTemplate] = useState("")
  const [count, setCount] = useState(1)
  const [concurrency, setConcurrency] = useState(5)
  const [method, setMethod] = useState<HttpMethod>("post")
  const [loading, setLoading] = useState(false)
  const [progress, setProgress] = useState(0)

  const abortRef = useRef(false)

  const parseCurl = (curl: string): ParsedCurl => {
    const url = curl.match(/curl\s+'([^']+)'/)?.[1]
    if (!url) throw new Error("URL not found")

    const methodMatch = curl.match(/-X\s+'?(\w+)'?/i)
    const detectedMethod = methodMatch
      ? (methodMatch[1].toLowerCase() as HttpMethod)
      : curl.includes("--data")
        ? "post"
        : "get"

    setMethod(detectedMethod)

    const body = curl.match(/--data-raw\s+'([\s\S]+)'/)?.[1]

    return { url, method: detectedMethod, body }
  }

  const handleCurlChange = (value: string) => {
    setCurl(value)

    const bodyMatch = value.match(/--data-raw\s+'([\s\S]+)'/)?.[1]
    if (bodyMatch) {
      try {
        const formatted = JSON.stringify(JSON.parse(bodyMatch), null, 2)
        setBodyTemplate(formatted)
      } catch {
        setBodyTemplate(bodyMatch)
      }
    }
  }

  const interpolateBody = (template: string, index: number): string => {
    return template.replace(/\{\{i\}\}/g, String(index))
  }

  // Паралельне виконання з обмеженням concurrency
  const runWithConcurrency = async <T,>(
    tasks: (() => Promise<T>)[],
    limit: number,
    onProgress: (completed: number) => void
  ): Promise<T[]> => {
    const results: T[] = []
    let completed = 0
    let index = 0

    const runNext = async (): Promise<void> => {
      if (abortRef.current) return

      const currentIndex = index++
      if (currentIndex >= tasks.length) return

      try {
        results[currentIndex] = await tasks[currentIndex]()
      } catch (e) {
        console.error(`Task ${currentIndex} failed:`, e)
      }

      completed++
      onProgress(completed)
      await runNext()
    }

    const workers = Array(Math.min(limit, tasks.length))
      .fill(null)
      .map(() => runNext())

    await Promise.all(workers)
    return results
  }

  const send = async () => {
    try {
      setLoading(true)
      setProgress(0)
      abortRef.current = false

      const parsed = parseCurl(curl)

      const tasks = Array.from({ length: count }, (_, i) => {
        const index = i + 1
        return async () => {
          const body = bodyTemplate
            ? JSON.parse(interpolateBody(bodyTemplate, index))
            : parsed.body
              ? JSON.parse(parsed.body)
              : undefined

          return request.api[method](parsed.url, body)
        }
      })

      await runWithConcurrency(tasks, concurrency, (completed) => {
        setProgress(Math.round((completed / count) * 100))
      })
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  const cancel = () => {
    abortRef.current = true
    setLoading(false)
  }

  return (
    <>
      <div className="fixed bottom-1 right-[9.5rem] z-50">
        <Tooltip title="Curl repeater">
          <Button
            type="primary"
            shape="circle"
            icon={<ThunderboltOutlined />}
            onClick={() => setOpen(true)}
          />
        </Tooltip>
      </div>

      <Drawer
        title="Curl repeater"
        placement="right"
        width={500}
        open={open}
        onClose={() => setOpen(false)}
      >
        <div className="flex flex-col gap-4">
          <div>
            <Text type="secondary" className="mb-1 block">
              Paste curl command:
            </Text>
            <TextArea
              rows={6}
              placeholder="curl 'https://...' -H '...' --data-raw '...'"
              value={curl}
              onChange={(e) => handleCurlChange(e.target.value)}
            />
          </div>

          <div>
            <Text type="secondary" className="mb-1 block">
              HTTP Method:
            </Text>
            <Select
              value={method}
              onChange={setMethod}
              className="w-full"
              options={[
                { value: "get", label: "GET" },
                { value: "post", label: "POST" },
                { value: "put", label: "PUT" },
                { value: "patch", label: "PATCH" },
                { value: "delete", label: "DELETE" }
              ]}
            />
          </div>

          <div>
            <Text type="secondary" className="mb-1 block">
              Body template (use <Text code>{"{{i}}"}</Text> for index):
            </Text>
            <TextArea
              rows={10}
              placeholder='{"name": "Item #{{i}}", "order": {{i}}}'
              value={bodyTemplate}
              onChange={(e) => setBodyTemplate(e.target.value)}
            />
          </div>

          <div className="flex gap-4">
            <div className="flex-1">
              <Text type="secondary" className="mb-1 block">
                Count:
              </Text>
              <Input
                type="number"
                min={1}
                max={1000}
                value={count}
                onChange={(e) => setCount(Number(e.target.value))}
              />
            </div>
            <div className="flex-1">
              <Text type="secondary" className="mb-1 block">
                Concurrency: {concurrency}
              </Text>
              <Slider
                min={1}
                max={20}
                value={concurrency}
                onChange={setConcurrency}
              />
            </div>
          </div>

          {loading && (
            <Progress percent={progress} size="small" status="active" />
          )}

          <div className="flex gap-2">
            <Button
              type="primary"
              icon={<SendOutlined />}
              loading={loading}
              onClick={send}
              disabled={!curl}
              className="flex-1"
            >
              Send {count} request{count > 1 ? "s" : ""}
            </Button>

            {loading && (
              <Button danger onClick={cancel}>
                Cancel
              </Button>
            )}
          </div>
        </div>
      </Drawer>
    </>
  )
}
